import { useInfiniteQuery } from "@tanstack/react-query";

import axiosServices from "../utils/axios";

import type { MailboxKey } from "../utils/types/email";
import type { JobList } from "../utils/types/voyages";

interface FetchJobsApi {
  data: JobList[];
  total: number;
}
export const fetchJobsData = async (
  page: number,
  pageSize: number | null,
  version?: string,
  statusValue?: string,
  q?: string,
  sort?: {
    field: string;
    direction: string;
  },
  agentId?: string
) => {
  const params: any = {
    format: "berthdata",
    page: page + 1,
    sort: sort
      ? JSON.stringify([{ property: sort.field, direction: sort.direction }])
      : '[{"property":"billing_reference","direction":"ASC"}]',
  };

  if (pageSize) {
    params.start = page * pageSize;
    params.limit = pageSize;
  }

  // Only add version to params if it exists
  if (version) {
    params.version = version;
  }
  if (statusValue) {
    params.statusValue = statusValue;
  }
  if (q) {
    params.q = q;
  }
  if (agentId) {
    params.agent_id = agentId; // Common convention for user ID filtering
  }
  const response = await axiosServices.get("/jobs", {
    params,
  });
  return response.data;
};

export const getJobsData = async (version?: string) => {
  const params: Record<string, any> = {
    version: "all",
    page: 1,
    start: 0,
    limit: 25,
  };
  if (version) {
    params.version = version;
  }
  const response = await axiosServices.get<FetchJobsApi>("/jobs", {
    params,
  });
  return response.data;
};

export const getJobsList = async () => {
  const params: Record<string, any> = {
    version: "active",
    page: 1,
    start: 0,
    limit: 50,
    format: "berthdata",
    sort: JSON.stringify([
      {
        property: "billing_reference",
        direction: "ASC",
      },
    ]),
  };

  const response = await axiosServices.get("/jobs", { params });
  return response.data;
};

export const getJobsListWithBerthData = async (
  page: number,
  pageSize: number | null,
  version?: string,
  statusValue?: string,
  q?: string,
  sort?: {
    field: string;
    direction: string;
  },
  agentId?: string
) => {
  const params: any = {
    format: "berthdata",
    page:page+1,
    sort: sort
      ? JSON.stringify([{ property: sort.field, direction: sort.direction }])
      : '[{"property":"assigned_agent","direction":"ASC"}]',
  };

  if (pageSize) {
    params.start = page * pageSize;
    params.limit = pageSize;
  }

  // Only add version to params if it exists
  if (version) {
    params.version = version;
  }
  if (statusValue) {
    params.statusValue = statusValue;
  }
  if (q) {
    params.q = q;
  }
  if (agentId) {
    params.agent_id = agentId; // Common convention for user ID filtering
  }

  const response = await axiosServices.get("/jobs", { params });
  return response.data;
};


export interface JobEmailMessage {
  from_email?: string | null;
  mailbox?: MailboxKey | string | null;
  mailbox_address?: string | null;
  mailbox_key?: MailboxKey | string | null;
  mailboxAddress?: string | null;
  mailboxKey?: MailboxKey | string | null;
  message_id: string;
  parent_folder_id?: string | null;
  received_datetime?: string | null;
  subject?: string | null;
}

export const fetchJobEmails = async (
  jobId: string,
  page: number = 1,
  limit: number = 20,
) => {
  if (!jobId) {
    throw new Error("jobId is required for fetchJobEmails");
  }

  const response = await axiosServices.get(`/jobs/${encodeURIComponent(jobId)}/email-messages`, {
    params: { page, limit },
  });

  return response.data as {
    data: JobEmailMessage[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    success: boolean;
  };
};

export const fetchEmailAttachments = async (
  messageId: string,
  mailbox: MailboxKey,
) => {
  if (!messageId) {
    throw new Error("messageId is required for fetchEmailAttachments");
  }
  const response = await axiosServices.get(`/outlook/${encodeURIComponent(messageId)}`, {
    params: { mailbox },
  });
  return response.data as { data: { attachments: any[] }; success: boolean };
};

export const useJobEmails = (jobId: string | null, enabled: boolean = true) =>
  useInfiniteQuery({
    queryKey: ["jobEmails", jobId],
    queryFn: ({ pageParam }) => fetchJobEmails(jobId!, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination ?? {};
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: enabled && !!jobId,
  });
